import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { thesaurusService } from './thesaurusAPI';
import { LOADING_STATES } from '../../constants';
import type { RootState } from '../../app/store';
import { Thesaurus, CreateThesaurusRequest, UpdateThesaurusRequest, CombineThesaurusRequest } from '../../types/api';
import { executeProposal } from '../chat/chatSlice';
import { VOCABULAIRE_LABELS } from '../../components/thesaurus/vocabulaireLabels';

/**
 * Une phrase fixe par geste (`rejectWithValue`) : le message du serveur n'est plus
 * affiché (il dit « thésaurus ») ; le détail part dans la console.
 */
const E = VOCABULAIRE_LABELS.erreurs.personnels;

const refus = (geste: string, error: unknown): void => {
  console.error(`❌ [thesaurusSlice] ${geste} :`, error);
};

export interface ThesaurusState {
  data: Thesaurus[];
  activeThesaurus: Thesaurus | null;
  status: string;
  detailsStatus: string; // Pour le chargement des détails d'un thésaurus spécifique
  error?: string;
}

const initialState: ThesaurusState = {
  data: [],
  activeThesaurus: null,
  status: LOADING_STATES.IDLE,
  detailsStatus: LOADING_STATES.IDLE,
  error: undefined,
};

// Récupérer tous les thésaurus
export const fetchThesaurusAsync = createAsyncThunk(
  'thesaurus/fetchAll',
  async (params: { page?: number; limit?: number } = {}, { rejectWithValue }) => {
    try {
      const result = await thesaurusService.getAll(params);
      return result.data;
    } catch (error) {
      refus('thesaurus/fetchAll', error);
      return rejectWithValue(E.chargement);
    }
  }
);

// Récupérer un thésaurus par ID
export const fetchThesaurusByIdAsync = createAsyncThunk(
  'thesaurus/fetchById',
  async ({ id, includeDetails = false }: { id: string; includeDetails?: boolean }, { rejectWithValue }) => {
    try {
      return await thesaurusService.getById(id, includeDetails);
    } catch (error) {
      refus('thesaurus/fetchById', error);
      return rejectWithValue(E.chargementUn);
    }
  }
);

// Rechercher des thésaurus
export const searchThesaurusAsync = createAsyncThunk(
  'thesaurus/search',
  async (query: string, { rejectWithValue }) => {
    try {
      return await thesaurusService.search(query);
    } catch (error) {
      refus('thesaurus/search', error);
      return rejectWithValue(E.recherche);
    }
  }
);

// Créer un nouveau thésaurus
export const createThesaurusAsync = createAsyncThunk(
  'thesaurus/create',
  async (data: CreateThesaurusRequest, { rejectWithValue }) => {
    try {
      return await thesaurusService.create(data);
    } catch (error) {
      refus('thesaurus/create', error);
      return rejectWithValue(E.creation);
    }
  }
);

// Mettre à jour un thésaurus
export const updateThesaurusAsync = createAsyncThunk(
  'thesaurus/update',
  async ({ id, data }: { id: string; data: UpdateThesaurusRequest }, { rejectWithValue }) => {
    try {
      return await thesaurusService.update(id, data);
    } catch (error) {
      refus('thesaurus/update', error);
      return rejectWithValue(E.modification);
    }
  }
);

// Supprimer un thésaurus
export const deleteThesaurusAsync = createAsyncThunk(
  'thesaurus/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      await thesaurusService.delete(id);
      return id;
    } catch (error) {
      refus('thesaurus/delete', error);
      return rejectWithValue(E.suppression);
    }
  }
);

// Ajouter des entrées à un thésaurus
export const addEntriesToThesaurusAsync = createAsyncThunk(
  'thesaurus/addEntries',
  async ({ id, entreeIds }: { id: string; entreeIds: string[] }, { rejectWithValue }) => {
    try {
      return await thesaurusService.addEntries(id, entreeIds);
    } catch (error) {
      refus('thesaurus/addEntries', error);
      return rejectWithValue(E.ajoutConcepts);
    }
  }
);

// Retirer des entrées d'un thésaurus
export const removeEntriesFromThesaurusAsync = createAsyncThunk(
  'thesaurus/removeEntries',
  async ({ id, entreeIds }: { id: string; entreeIds: string[] }, { rejectWithValue }) => {
    try {
      return await thesaurusService.removeEntries(id, entreeIds);
    } catch (error) {
      refus('thesaurus/removeEntries', error);
      return rejectWithValue(E.retraitConcepts);
    }
  }
);

// Combiner plusieurs thésaurus
export const combineThesaurusAsync = createAsyncThunk(
  'thesaurus/combine',
  async (data: CombineThesaurusRequest, { rejectWithValue }) => {
    try {
      return await thesaurusService.combine(data);
    } catch (error) {
      refus('thesaurus/combine', error);
      return rejectWithValue(E.combinaison);
    }
  }
);

// Dégrouper un thésaurus composite lié
export const unlinkThesaurusAsync = createAsyncThunk(
  'thesaurus/unlink',
  async (id: string, { rejectWithValue }) => {
    try {
      return await thesaurusService.unlink(id);
    } catch (error) {
      refus('thesaurus/unlink', error);
      return rejectWithValue(E.degroupage);
    }
  }
);

// Anciens thunks pour compatibilité
export const thesaurusAsync = createAsyncThunk(
  'thesaurus/get',
  async (_, { rejectWithValue }) => {
    try {
      return await thesaurusService.thesaurus();
    } catch (error) {
      refus('thesaurus/get', error);
      return rejectWithValue(E.chargement);
    }
  }
);

export const saveAsync = createAsyncThunk(
  'thesaurus/save',
  async (thesaurus: Partial<Thesaurus>, { rejectWithValue }) => {
    try {
      return await thesaurusService.save(thesaurus);
    } catch (error) {
      refus('thesaurus/save', error);
      return rejectWithValue(E.enregistrement);
    }
  }
);

export const thesaurusSlice = createSlice({
  name: 'thesaurus',
  initialState,
  reducers: {
    activateThesaurus(state, action: PayloadAction<Thesaurus>) {
      state.activeThesaurus = action.payload;
    },
    clearActiveThesaurus(state) {
      state.activeThesaurus = null;
    },
    setThesaurusLoading(state) {
      state.status = LOADING_STATES.LOADING;
      state.error = undefined;
    },
    setThesaurusError(state, action: PayloadAction<string>) {
      state.status = LOADING_STATES.FAILED;
      state.error = action.payload;
    },
    setThesaurusSucceeded(state) {
      state.status = LOADING_STATES.SUCCEEDED;
      state.error = undefined;
    },
    /** Efface l'erreur affichée (alerte fermée). */
    clearError(state) {
      state.error = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchThesaurusAsync
      .addCase(fetchThesaurusAsync.pending, (state) => {
        state.status = LOADING_STATES.LOADING;
        state.error = undefined;
      })
      .addCase(fetchThesaurusAsync.fulfilled, (state, action) => {
        console.log('📦 [thesaurusSlice] fetchThesaurusAsync.fulfilled - payload:', action.payload?.length, 'thésaurus', action.payload?.map((t: any) => t.nom));
        state.data = action.payload || [];
        state.status = LOADING_STATES.SUCCEEDED;
        state.error = undefined;
      })
      .addCase(fetchThesaurusAsync.rejected, (state, action) => {
        state.status = LOADING_STATES.FAILED;
        state.error = (action.payload as string | undefined) ?? E.chargement;
      })
      // fetchThesaurusByIdAsync - utilise detailsStatus au lieu de status global
      .addCase(fetchThesaurusByIdAsync.pending, (state) => {
        state.detailsStatus = LOADING_STATES.LOADING;
        state.error = undefined;
      })
      .addCase(fetchThesaurusByIdAsync.fulfilled, (state, action) => {
        console.log('✅ fetchThesaurusByIdAsync réussi:', {
          id: action.payload._id,
          nom: action.payload.nom,
          hasEntrees: !!action.payload.entrees,
          entreesCount: action.payload.entrees?.length,
          hasSubThesaurus: !!action.payload.subThesaurus,
          subThesaurusCount: action.payload.subThesaurus?.length,
          payload: action.payload
        });
        state.activeThesaurus = action.payload;
        state.detailsStatus = LOADING_STATES.SUCCEEDED;
        state.error = undefined;
      })
      .addCase(fetchThesaurusByIdAsync.rejected, (state, action) => {
        state.detailsStatus = LOADING_STATES.FAILED;
        state.error = (action.payload as string | undefined) ?? E.chargementUn;
      })
      // searchThesaurusAsync
      .addCase(searchThesaurusAsync.pending, (state) => {
        state.status = LOADING_STATES.LOADING;
        state.error = undefined;
      })
      .addCase(searchThesaurusAsync.fulfilled, (state, action) => {
        state.data = action.payload || [];
        state.status = LOADING_STATES.SUCCEEDED;
        state.error = undefined;
      })
      .addCase(searchThesaurusAsync.rejected, (state, action) => {
        state.status = LOADING_STATES.FAILED;
        state.error = (action.payload as string | undefined) ?? E.recherche;
      })
      // createThesaurusAsync
      .addCase(createThesaurusAsync.pending, (state) => {
        state.status = LOADING_STATES.LOADING;
        state.error = undefined;
      })
      .addCase(createThesaurusAsync.fulfilled, (state, action) => {
        state.data.push(action.payload);
        state.activeThesaurus = action.payload;
        state.status = LOADING_STATES.SUCCEEDED;
        state.error = undefined;
      })
      .addCase(createThesaurusAsync.rejected, (state, action) => {
        state.status = LOADING_STATES.FAILED;
        state.error = (action.payload as string | undefined) ?? E.creation;
      })
      // updateThesaurusAsync
      .addCase(updateThesaurusAsync.pending, (state) => {
        state.status = LOADING_STATES.LOADING;
        state.error = undefined;
      })
      .addCase(updateThesaurusAsync.fulfilled, (state, action) => {
        const idx = state.data.findIndex(t => t._id === action.payload._id);
        if (idx > -1) {
          state.data[idx] = action.payload;
        }
        if (state.activeThesaurus?._id === action.payload._id) {
          state.activeThesaurus = action.payload;
        }
        state.status = LOADING_STATES.SUCCEEDED;
        state.error = undefined;
      })
      .addCase(updateThesaurusAsync.rejected, (state, action) => {
        state.status = LOADING_STATES.FAILED;
        state.error = (action.payload as string | undefined) ?? E.modification;
      })
      // deleteThesaurusAsync
      .addCase(deleteThesaurusAsync.pending, (state) => {
        state.status = LOADING_STATES.LOADING;
        state.error = undefined;
      })
      .addCase(deleteThesaurusAsync.fulfilled, (state, action) => {
        state.data = state.data.filter(t => t._id !== action.payload);
        if (state.activeThesaurus?._id === action.payload) {
          state.activeThesaurus = null;
        }
        state.status = LOADING_STATES.SUCCEEDED;
        state.error = undefined;
      })
      .addCase(deleteThesaurusAsync.rejected, (state, action) => {
        state.status = LOADING_STATES.FAILED;
        state.error = (action.payload as string | undefined) ?? E.suppression;
      })
      // addEntriesToThesaurusAsync
      .addCase(addEntriesToThesaurusAsync.pending, (state) => {
        state.status = LOADING_STATES.LOADING;
        state.error = undefined;
      })
      .addCase(addEntriesToThesaurusAsync.fulfilled, (state, action) => {
        const idx = state.data.findIndex(t => t._id === action.payload._id);
        if (idx > -1) {
          state.data[idx] = action.payload;
        }
        if (state.activeThesaurus?._id === action.payload._id) {
          state.activeThesaurus = action.payload;
        }
        state.status = LOADING_STATES.SUCCEEDED;
        state.error = undefined;
      })
      .addCase(addEntriesToThesaurusAsync.rejected, (state, action) => {
        state.status = LOADING_STATES.FAILED;
        state.error = (action.payload as string | undefined) ?? E.ajoutConcepts;
      })
      // removeEntriesFromThesaurusAsync
      .addCase(removeEntriesFromThesaurusAsync.pending, (state) => {
        state.status = LOADING_STATES.LOADING;
        state.error = undefined;
      })
      .addCase(removeEntriesFromThesaurusAsync.fulfilled, (state, action) => {
        const idx = state.data.findIndex(t => t._id === action.payload._id);
        if (idx > -1) {
          state.data[idx] = action.payload;
        }
        if (state.activeThesaurus?._id === action.payload._id) {
          state.activeThesaurus = action.payload;
        }
        state.status = LOADING_STATES.SUCCEEDED;
        state.error = undefined;
      })
      .addCase(removeEntriesFromThesaurusAsync.rejected, (state, action) => {
        state.status = LOADING_STATES.FAILED;
        state.error = (action.payload as string | undefined) ?? E.retraitConcepts;
      })
      // combineThesaurusAsync
      .addCase(combineThesaurusAsync.pending, (state) => {
        state.status = LOADING_STATES.LOADING;
        state.error = undefined;
      })
      .addCase(combineThesaurusAsync.fulfilled, (state, action) => {
        state.data.push(action.payload);
        state.activeThesaurus = action.payload;
        state.status = LOADING_STATES.SUCCEEDED;
        state.error = undefined;
      })
      .addCase(combineThesaurusAsync.rejected, (state, action) => {
        state.status = LOADING_STATES.FAILED;
        state.error = (action.payload as string | undefined) ?? E.combinaison;
      })
      // unlinkThesaurusAsync
      .addCase(unlinkThesaurusAsync.pending, (state) => {
        state.status = LOADING_STATES.LOADING;
        state.error = undefined;
      })
      .addCase(unlinkThesaurusAsync.fulfilled, (state, action) => {
        const idx = state.data.findIndex(t => t._id === action.payload._id);
        if (idx > -1) {
          state.data[idx] = action.payload;
        }
        if (state.activeThesaurus?._id === action.payload._id) {
          state.activeThesaurus = action.payload;
        }
        state.status = LOADING_STATES.SUCCEEDED;
        state.error = undefined;
      })
      .addCase(unlinkThesaurusAsync.rejected, (state, action) => {
        state.status = LOADING_STATES.FAILED;
        state.error = (action.payload as string | undefined) ?? E.degroupage;
      })
      // Anciens thunks (compatibilité)
      .addCase(thesaurusAsync.pending, (state) => {
        state.status = LOADING_STATES.LOADING;
        state.error = undefined;
      })
      .addCase(thesaurusAsync.fulfilled, (state, action) => {
        state.data = action.payload || [];
        state.status = LOADING_STATES.SUCCEEDED;
        state.error = undefined;
      })
      .addCase(thesaurusAsync.rejected, (state, action) => {
        state.status = LOADING_STATES.FAILED;
        state.error = (action.payload as string | undefined) ?? E.chargement;
      })
      .addCase(saveAsync.pending, (state) => {
        state.status = LOADING_STATES.LOADING;
        state.error = undefined;
      })
      .addCase(saveAsync.fulfilled, (state, action) => {
        const idx = state.data.findIndex(l => l._id === action.payload._id);
        if (idx > -1) {
          state.data[idx] = action.payload;
        } else {
          state.data.push(action.payload);
          state.activeThesaurus = action.payload;
        }
        state.status = LOADING_STATES.SUCCEEDED;
        state.error = undefined;
      })
      .addCase(saveAsync.rejected, (state, action) => {
        state.status = LOADING_STATES.FAILED;
        state.error = (action.payload as string | undefined) ?? E.enregistrement;
      })

      // ── Cross-slice : thésaurus créé/modifié/supprimé via une proposition RAG Écriture ──
      // Sans ça, valider « créer un thésaurus » dans l'Assistant écrivait en base
      // mais laissait `data` intact : SecThesaurus ne rechargeant la liste que si
      // elle est vide, il fallait rafraîchir la page pour voir le thésaurus.
      .addCase(executeProposal.fulfilled, (state, action) => {
        const result = action.payload;
        if (!result.success || result.targetType !== 'thesaurus') return;

        const resultData = result.resultData as Thesaurus | undefined;
        const targetId = resultData?._id || result.resultId;
        if (!targetId) return;

        if (result.action === 'delete') {
          state.data = state.data.filter(t => t._id !== targetId);
          if (state.activeThesaurus?._id === targetId) {
            state.activeThesaurus = null;
          }
          return;
        }

        if (!resultData) return;

        const idx = state.data.findIndex(t => t._id === targetId);
        if (idx > -1) {
          state.data[idx] = resultData;
        } else if (state.status !== LOADING_STATES.IDLE) {
          // Liste jamais chargée (idle) : ne rien injecter, sinon la garde
          // `status === 'idle' && data.length === 0` de SecThesaurus empêcherait
          // le chargement complet et n'afficherait que ce seul thésaurus.
          state.data.push(resultData);
        }

        if (state.activeThesaurus?._id === targetId) {
          state.activeThesaurus = resultData;
        }
      });
  },
});

export const selectThesaurus = (state: RootState) => state.thesaurus.data;
export const selectActiveThesaurus = (state: RootState) => state.thesaurus.activeThesaurus;
export const getThesaurusStatus = (state: RootState) => state.thesaurus.status;
export const getThesaurusDetailsStatus = (state: RootState) => state.thesaurus.detailsStatus;
export const getThesaurusError = (state: RootState) => state.thesaurus.error;
export default thesaurusSlice.reducer;
export const { 
  activateThesaurus, 
  clearActiveThesaurus,
  setThesaurusLoading, 
  setThesaurusError, 
  setThesaurusSucceeded,
  clearError
} = thesaurusSlice.actions;
